import { useEffect, useRef, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import AnimatedSection from '../components/AnimatedSection';
import { ContentContext } from '../contexts/ContentContext';
import '../styles/home.css';

/* ============================================================
   GLOBE ORBIT
   ============================================================ */
function GlobeOrbit() {
  return (
    <div className="hero-globe" aria-hidden="true">
      <div className="hero-globe__glow" />
      <div className="hero-globe__ring hero-globe__ring--3" />
      <div className="hero-globe__ring hero-globe__ring--2">
        <div className="hero-globe__dot hero-globe__dot--2" />
      </div>
      <div className="hero-globe__ring hero-globe__ring--1">
        <div className="hero-globe__dot hero-globe__dot--1" />
      </div>
      <div className="hero-globe__core" />
    </div>
  );
}

/* ============================================================
   ANIMATED CHAT LOOP
   ============================================================ */
const CHAT_SEQ = [
  { from: 'user', text: "Hi, I'd like to book an appointment" },
  { from: 'ai',   text: 'Of course! I can help with that right away.' },
  { from: 'ai',   text: "You're all set for Thursday at 2pm!" },
];

function AnimatedChat() {
  const [shown, setShown]   = useState([]);
  const [typing, setTyping] = useState(false);
  const timers = useRef([]);

  useEffect(() => {
    const push = (fn, ms) => { const t = setTimeout(fn, ms); timers.current.push(t); };
    const clear = () => { timers.current.forEach(clearTimeout); timers.current = []; };

    function run() {
      clear();
      setShown([]); setTyping(false);
      let d = 600;
      CHAT_SEQ.forEach((_, i) => {
        push(() => setTyping(true), d);
        d += 950;
        push(() => { setTyping(false); setShown(p => [...p, i]); }, d);
        d += 750;
      });
      push(run, d + 2400);
    }
    run();
    return clear;
  }, []);

  return (
    <div className="chat-mockup chat-mockup--loop">
      <div className="chat-mockup__header">
        <div className="chat-mockup__logo-placeholder">Y</div>
        <div className="chat-mockup__brand">
          <div className="chat-mockup__brand-name">Your Logo Here</div>
          <div className="chat-mockup__brand-sub">AI Assistant</div>
        </div>
        <div className="chat-mockup__online">
          <div className="chat-mockup__online-dot" />Online
        </div>
      </div>
      <div className="chat-mockup__messages chat-mockup__messages--loop">
        {CHAT_SEQ.map((msg, i) =>
          shown.includes(i) ? (
            <div key={i} className={`chat-bubble chat-bubble--${msg.from} chat-bubble--pop`}>
              {msg.text}
            </div>
          ) : null
        )}
        {typing && (
          <div className="chat-bubble chat-bubble--ai chat-bubble--typing-indicator">
            <span className="tdot" /><span className="tdot" /><span className="tdot" />
          </div>
        )}
      </div>
      <div className="chat-mockup__input">
        <span className="chat-mockup__input-text">Type a message...</span>
        <div className="chat-mockup__send">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13"/>
            <polygon points="22 2 15 22 11 13 2 9 22 2"/>
          </svg>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   CIRCULAR STAT RING
   ============================================================ */
function useCountUp(target, duration = 2000, suffix = '') {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const startTime = performance.now();
        const isFloat = !Number.isInteger(target);
        const tick = (now) => {
          const progress = Math.min((now - startTime) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          const current = isFloat ? parseFloat((eased * target).toFixed(1)) : Math.round(eased * target);
          setValue(current);
          if (progress < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.5 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, duration]);
  return { ref, display: value + suffix };
}

function CircularStat({ prefix = '', target, suffix = '', label, fillPct = 100, idx = 0 }) {
  const { ref: numRef, display } = useCountUp(target, 1800, suffix);
  const [progress, setProgress] = useState(0);
  const wrapRef = useRef(null);
  const observed = useRef(false);
  const R = 40;
  const C = 2 * Math.PI * R;
  const gradId = `sg${idx}`;

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !observed.current) {
        observed.current = true;
        setTimeout(() => setProgress(fillPct), 300);
      }
    }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [fillPct]);

  const offset = C - (progress / 100) * C;

  return (
    <div className="stat-ring-item animate-child" ref={wrapRef}>
      <div className="stat-ring-wrap" ref={numRef}>
        <svg viewBox="0 0 100 100" className="stat-ring-svg">
          <defs>
            <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#4F8EF7" />
              <stop offset="100%" stopColor="#6366f1" />
            </linearGradient>
          </defs>
          <circle cx="50" cy="50" r={R} fill="none" strokeWidth="5" stroke="rgba(255,255,255,0.06)" />
          <circle
            cx="50" cy="50" r={R} fill="none" strokeWidth="5"
            stroke={`url(#${gradId})`} strokeLinecap="round"
            strokeDasharray={C} strokeDashoffset={offset}
            style={{
              transition: 'stroke-dashoffset 1.8s cubic-bezier(0.4,0,0.2,1)',
              transformOrigin: '50% 50%',
              transform: 'rotate(-90deg)',
            }}
          />
        </svg>
        <div className="stat-ring-number">{prefix}{display}</div>
      </div>
      <div className="stat-ring-label">{label}</div>
    </div>
  );
}

/* ============================================================
   CHANNEL SWITCHER
   ============================================================ */
const CHANNEL_TABS = [
  { id: 'voice',     label: 'Voice Calls' },
  { id: 'chat',      label: 'Web Chat' },
  { id: 'whatsapp',  label: 'WhatsApp' },
  { id: 'social',    label: 'Social DMs' },
];

function VoiceMockup() {
  return (
    <div className="chmock chmock--voice">
      <div className="chmock__title">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2"><circle cx="12" cy="12" r="10" /></svg>
        Incoming Call — AI Answering
      </div>
      <div className="chmock__caller">+1 (555) 234-5678</div>
      <div className="chmock__timer">00:42</div>
      <div className="chmock__waveform">
        {Array.from({ length: 18 }).map((_, i) => (
          <div key={i} className="chmock__wave-bar" style={{ animationDelay: `${i * 0.09}s` }} />
        ))}
      </div>
      <div className="chmock__status">AI is qualifying lead and booking appointment...</div>
    </div>
  );
}

function WhatsAppMockup() {
  return (
    <div className="chmock chmock--sms">
      <div className="chmock__title" style={{ color: '#25D366' }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.553 4.116 1.52 5.845L0 24l6.335-1.496A11.96 11.96 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.956 0-3.792-.5-5.388-1.38l-.385-.228-3.993.943.957-3.892-.249-.4A10 10 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
        WhatsApp Business — Live
      </div>
      <div className="chmock__sms-msgs">
        <div className="chmock__sms-bubble chmock__sms-bubble--in">Hi! Do you have appointments available this week?</div>
        <div className="chmock__sms-bubble chmock__sms-bubble--out">Yes! We have openings Tuesday at 10am and Thursday at 2pm. Which works for you?</div>
        <div className="chmock__sms-bubble chmock__sms-bubble--in">Thursday at 2pm please</div>
        <div className="chmock__sms-bubble chmock__sms-bubble--out">Confirmed! ✅ Thursday 2:00 PM — you'll get a reminder 1 hour before.</div>
      </div>
    </div>
  );
}

function SocialMockup() {
  return (
    <div className="chmock chmock--webchat">
      <div className="chmock__chat-header">
        <div className="chmock__chat-logo" style={{ background: 'linear-gradient(135deg,#f58529,#dd2a7b,#515bd4)' }}>IG</div>
        <div>
          <div className="chmock__chat-name">Instagram / Facebook DMs</div>
          <div className="chmock__chat-online">● AI is active</div>
        </div>
      </div>
      <div className="chmock__chat-msgs">
        <div className="chmock__chat-bubble chmock__chat-bubble--user">Saw your post! How much does it cost?</div>
        <div className="chmock__chat-bubble chmock__chat-bubble--ai">Great question! Our packages start at $X. Want me to send you all the details or book a quick call?</div>
        <div className="chmock__chat-bubble chmock__chat-bubble--user">Book a call please</div>
        <div className="chmock__chat-bubble chmock__chat-bubble--ai">Done! I've reserved a 15-min slot for you — check your DMs for the calendar link. 📅</div>
      </div>
      <div className="chmock__chat-input">Reply...</div>
    </div>
  );
}

function WebChatMockup() {
  return (
    <div className="chmock chmock--webchat">
      <div className="chmock__chat-header">
        <div className="chmock__chat-logo">Y</div>
        <div>
          <div className="chmock__chat-name">Your Brand Assistant</div>
          <div className="chmock__chat-online">● Online now</div>
        </div>
      </div>
      <div className="chmock__chat-msgs">
        <div className="chmock__chat-bubble chmock__chat-bubble--ai">Hello! How can I help you today?</div>
        <div className="chmock__chat-bubble chmock__chat-bubble--user">I need to book an appointment</div>
        <div className="chmock__chat-bubble chmock__chat-bubble--ai">Of course! What day works best for you?</div>
        <div className="chmock__chat-bubble chmock__chat-bubble--user">Friday afternoon</div>
        <div className="chmock__chat-bubble chmock__chat-bubble--ai">You're all set for Friday at 3:00 PM. Confirmation sent! ✓</div>
      </div>
      <div className="chmock__chat-input">Type your message...</div>
    </div>
  );
}

const CHANNEL_MOCKUPS = {
  voice:    <VoiceMockup />,
  chat:     <WebChatMockup />,
  whatsapp: <WhatsAppMockup />,
  social:   <SocialMockup />,
};

function ChannelSwitcher() {
  const [active, setActive] = useState('voice');
  const [fading, setFading] = useState(false);

  const handleSwitch = (id) => {
    if (id === active) return;
    setFading(true);
    setTimeout(() => { setActive(id); setFading(false); }, 250);
  };

  return (
    <section className="channels-switch" aria-label="One platform, every channel">
      <div className="channels-switch__inner">
        <AnimatedSection>
          <div className="channels-switch__header">
            <span className="section-label">Communication</span>
            <h2>One Platform. Every Channel Your Customers Use.</h2>
            <p>Voice calls, website chat, WhatsApp, Instagram DMs, Facebook Messenger — your AI agent handles all of them, from a single place.</p>
          </div>
        </AnimatedSection>
        <AnimatedSection delay={0.1}>
          <div className="channels-switch__layout">
            <div className="channels-switch__tabs" role="tablist">
              {CHANNEL_TABS.map(ch => (
                <button
                  key={ch.id}
                  role="tab"
                  aria-selected={active === ch.id}
                  className={`ch-tab ${active === ch.id ? 'ch-tab--active' : ''}`}
                  onClick={() => handleSwitch(ch.id)}
                >
                  {ch.label}
                </button>
              ))}
            </div>
            <div className={`channels-switch__mockup ${fading ? 'fade-out' : 'fade-in'}`}>
              {CHANNEL_MOCKUPS[active]}
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

/* ============================================================
   INDUSTRY SELECTOR
   ============================================================ */
const CHECK_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const INDUSTRY_DATA = [
  {
    name: 'Real Estate',
    bullets: [
      'A realtor stops losing leads after hours — the AI qualifies callers and books showings while they sleep',
      'Buyers get instant property info and listing details without waiting for a callback',
      'Every prospect gets a follow-up automatically, so no deal slips through the cracks',
    ],
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  },
  {
    name: 'Medical',
    bullets: [
      'A clinic fills its calendar automatically — patients book and confirm appointments without calling',
      'Staff stop fielding routine calls; the AI handles reminders and cancellations instantly',
      'Patients complete intake forms before their visit, saving time for everyone on the day',
    ],
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>,
  },
  {
    name: 'Legal',
    bullets: [
      'A law firm converts more website visitors into booked consultations — without a receptionist',
      'Prospects get answers to common questions immediately, so they arrive warmer and more ready to hire',
      'No lead goes cold — the AI follows up automatically between touchpoints',
    ],
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1v22M17 5H9.5a3.5 3.5 0 1 0 0 7h5a3.5 3.5 0 1 1 0 7H6"/></svg>,
  },
  {
    name: 'E-Commerce',
    bullets: [
      'Customers get order updates and returns handled instantly — no support ticket queue',
      'Product questions get answered automatically, so buyers don\'t leave to shop elsewhere',
      'Abandoned carts get recovered with smart follow-up messages sent at the right moment',
    ],
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>,
  },
  {
    name: 'Services',
    bullets: [
      'A trades business stops missing jobs — the AI answers every call and books the appointment',
      'Quotes and confirmations go out automatically, so the team focuses on the work, not the admin',
      'After every job, the AI follows up for a review and offers to rebook — revenue without extra effort',
    ],
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>,
  },
];

function IndustrySelector({ industryItems, heading }) {
  const [active, setActive] = useState(0);
  const [fading, setFading] = useState(false);

  const handleSelect = (i) => {
    if (i === active) return;
    setFading(true);
    setTimeout(() => { setActive(i); setFading(false); }, 220);
  };

  return (
    <section className="industry-select section" aria-label="Industries we serve">
      <div className="industry-select__inner">
        <AnimatedSection>
          <div className="industry-select__header">
            <span className="section-label">Use Cases</span>
            <h2>{heading || 'See How It Works in Your Industry'}</h2>
          </div>
        </AnimatedSection>
        <AnimatedSection delay={0.1}>
          <div className="industry-tabs" role="tablist">
            {industryItems.map((item, i) => (
              <button
                key={item.name}
                role="tab"
                aria-selected={active === i}
                className={`industry-tab ${active === i ? 'industry-tab--active' : ''}`}
                onClick={() => handleSelect(i)}
              >
                {item.name}
              </button>
            ))}
          </div>
          <div className={`industry-content ${fading ? 'fade-out' : 'fade-in'}`}>
            <div className="industry-content__icon">{industryItems[active]?.icon}</div>
            <div className="industry-content__text">
              <h3>{industryItems[active]?.name}</h3>
              <ul className="industry-content__list">
                {(industryItems[active]?.bullets || []).map((b, i) => (
                  <li key={i}>{CHECK_ICON}{b}</li>
                ))}
              </ul>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

/* ============================================================
   HOME PAGE
   ============================================================ */
export default function Home() {
  const { content } = useContext(ContentContext);
  const hero        = content.hero       || {};
  const ben         = content.benefits   || {};
  const hiw         = content.howItWorks || {};
  const ind         = content.industries || {};

  const benefitItems = (ben.items || BENEFITS).map((item, i) => ({
    ...item,
    icon: BENEFITS[i]?.icon,
    desc: item.desc || item.description || '',
  }));

  const industryItems = (ind.items || INDUSTRY_DATA).map((item, i) => ({
    ...item,
    bullets: item.bullets || [],
    icon: INDUSTRY_DATA[i]?.icon,
  }));

  const hiwSteps = (hiw.steps || STEPS).map(s => ({
    num:   s.num   || s.number || '',
    title: s.title || '',
    desc:  s.desc  || s.description || '',
  }));

  return (
    <main>

      {/* ===== HERO ===== */}
      <section className="hero" aria-label="Hero">
        <div className="hero__blobs" aria-hidden="true">
          <div className="hero__blob hero__blob--1" />
          <div className="hero__blob hero__blob--2" />
          <div className="hero__blob hero__blob--3" />
        </div>
        <div className="hero__particles" aria-hidden="true">
          {Array.from({ length: 20 }).map((_, i) => <div key={i} className="hero__particle" />)}
        </div>

        <div className="hero__layout">
          <div className="hero__content">
            <div className="hero__label">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="#4F8EF7">
                <path d="M5 0 L6.18 3.82 L10 5 L6.18 6.18 L5 10 L3.82 6.18 L0 5 L3.82 3.82 Z"/>
              </svg>
              {hero.label || 'AI-Powered Customer Service for Your Business'}
            </div>

            <h1 className="hero__title">
              {hero.headline1 || 'Your Business, Answering Every Customer.'}
              <span className="line2">{hero.headline2 || '24 Hours a Day.'}</span>
            </h1>

            <p className="hero__subtext">
              {hero.subtext || 'A branded AI assistant that answers calls, books appointments, and handles inquiries — automatically, around the clock.'}
            </p>

            <div className="hero__buttons">
              <Link to={hero.btn1Url || '/contact'} className="btn-primary" aria-label="Book a free demo">
                {hero.btn1Label || 'Book My Free 30-Min Demo →'}
              </Link>
              <Link to={hero.btn2Url || '/how-it-works'} className="btn-secondary" aria-label="See how it works">
                {hero.btn2Label || 'See How It Works'}
              </Link>
            </div>

            <p className="hero__trust">
              {hero.trustText || 'No long-term contracts · Your brand, not ours · Live in under 24 hours'}
            </p>
          </div>

          <div className="hero__globe-wrap" aria-hidden="true">
            <GlobeOrbit />
          </div>
        </div>
      </section>

      {/* ===== STATS BAR ===== */}
      <section className="stats-bar" aria-label="Key statistics">
        <AnimatedSection>
          <div className="stats-bar__inner">
            <CircularStat target={24}  suffix="/7" label="Always-On Coverage"   fillPct={100} idx={0} />
            <CircularStat target={5}   suffix="+"  label="Channels Supported"   fillPct={80}  idx={1} />
            <CircularStat target={100} suffix="%" label="Your Brand & Domain"   fillPct={100} idx={2} />
            <CircularStat target={160} suffix="+" label="Languages Supported"   fillPct={100} idx={3} />
          </div>
        </AnimatedSection>
      </section>

      {/* ===== ABOUT ===== */}
      <section className="about section" aria-label="About Klaivox">
        <div className="about__inner">
          <AnimatedSection>
            <div className="about__text">
              <span className="section-label">About Klaivox</span>
              <h2>Your AI Assistant, Branded as Your Business</h2>
              <p>
                Imagine your phone being answered 24/7, appointments booked automatically, and every
                customer inquiry handled instantly — by an AI assistant that carries <em>your</em>
                business name, your logo, and your voice.
              </p>
              <p>
                That's Klaivox. You tell us about your business, we set everything up, and your
                customers get an experience that feels like talking to your best team member — any
                time of day or night.
              </p>
              <p className="about__whitebox">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                <strong>Your AI carries your brand — customers see your name, your logo, and your domain. Klaivox stays invisible.</strong>
              </p>
              <Link to="/features" className="about__link">
                See everything the platform does →
              </Link>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.15}>
            <AnimatedChat />
          </AnimatedSection>
        </div>
      </section>

      {/* ===== BENEFITS ===== */}
      <section className="benefits section" aria-label="Key benefits">
        <div className="benefits__inner">
          <AnimatedSection>
            <div className="benefits__header">
              <span className="section-label">Why Klaivox</span>
              <h2>{ben.heading || 'Stop Losing Leads After Hours — Let AI Handle It for You'}</h2>
            </div>
          </AnimatedSection>
          <AnimatedSection>
            <div className="benefits__grid">
              {benefitItems.map((b, i) => (
                <div className="benefit-card animate-child" key={i}>
                  <div className="benefit-card__icon">{b.icon}</div>
                  <h3>{b.title}</h3>
                  <p>{b.desc}</p>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ===== WHO IS THIS FOR ===== */}
      <section className="who-section section" aria-label="Who is this for">
        <div className="who-section__inner">
          <AnimatedSection>
            <div className="who-section__header">
              <span className="section-label">Who It's For</span>
              <h2>Built for Any Business That Talks to Customers</h2>
              <p>If your business handles calls, inquiries, or bookings — Klaivox can automate it.</p>
            </div>
          </AnimatedSection>
          <AnimatedSection delay={0.1}>
            <div className="who-cards">
              {WHO_PERSONAS.map((p, i) => (
                <div className="who-card animate-child" key={i}>
                  <div className="who-card__icon">{p.icon}</div>
                  <h3>{p.title}</h3>
                  <p>{p.desc}</p>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ===== CHANNEL SWITCHER ===== */}
      <ChannelSwitcher />

      {/* ===== INDUSTRY SELECTOR ===== */}
      <IndustrySelector industryItems={industryItems} heading={ind.heading} />

      {/* ===== PROCESS PREVIEW ===== */}
      <section className="process-preview section" aria-label="How it works overview">
        <div className="process-preview__inner">
          <AnimatedSection>
            <div className="process-preview__header">
              <span className="section-label">The Process</span>
              <h2>{hiw.heading || 'From Zero to Live AI Agent in Under 24 Hours'}</h2>
            </div>
          </AnimatedSection>
          <AnimatedSection>
            <div className="process-steps">
              {hiwSteps.map((s, i) => (
                <div className="process-step animate-child" key={i}>
                  <div className="process-step__number">{s.num}</div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
              ))}
            </div>
          </AnimatedSection>
          <div className="process-preview__cta">
            <Link to="/onboarding">See the full process →</Link>
          </div>
        </div>
      </section>

      {/* ===== CTA BANNER ===== */}
      <section className="cta-banner" aria-label="Call to action">
        <div className="cta-banner__blob" aria-hidden="true" />
        <AnimatedSection>
          <div className="cta-banner__inner">
            <h2>See Your Business Running on AI — Live, in 30 Minutes</h2>
            <p className="cta-banner__sub">
              No long-term contracts<span>·</span>Your brand, not ours<span>·</span>Live in under 24 hours
            </p>
            <Link to="/contact" className="btn-primary" aria-label="Book a free demo">
              Book My Free 30-Min Demo →
            </Link>
          </div>
        </AnimatedSection>
      </section>

    </main>
  );
}

/* ---- Data ---- */

const BENEFITS = [
  {
    title: 'Your Brand, Your AI',
    desc: 'Your customers see your business name, logo, and domain on every interaction. The AI feels like it\'s part of your team — because to them, it is.',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  },
  {
    title: 'Never Miss a Lead Again',
    desc: 'Your AI answers every call, replies to every message, and books every appointment — even at 2am on a Sunday. Instant responses, zero staff needed.',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
  },
  {
    title: 'Appointments Booked Automatically',
    desc: 'Your AI checks your calendar, offers available slots, confirms the booking, and sends reminders — all without any staff involvement.',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  },
  {
    title: 'Live in Under 24 Hours',
    desc: 'No technical skills needed. We configure everything — branding, channels, AI training, and integrations — on a single setup call. You could be live by tomorrow.',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>,
  },
  {
    title: '160+ Languages Supported',
    desc: 'Your AI speaks to customers in their language — English, French, Spanish, Mandarin, and 160+ more. Serve a diverse customer base without hiring multilingual staff.',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
  },
  {
    title: 'One Flat Monthly Fee',
    desc: 'No per-conversation fees, no per-seat charges, no surprise invoices. From $29 CAD/month — all channels, all integrations, all support.',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>,
  },
];

const STEPS = [
  { num: '01', title: 'Book Your Setup Call',   desc: 'Schedule a free onboarding session with our team.' },
  { num: '02', title: 'Connect Your Brand',     desc: 'We apply your logo, colors, and custom domain.' },
  { num: '03', title: 'Train Your AI Agent',    desc: 'Configure knowledge, tone, and automated workflows.' },
  { num: '04', title: 'Go Live & Start Earning', desc: 'Launch your branded AI and start invoicing clients immediately.' },
];

const WHO_PERSONAS = [
  {
    title: 'Service Businesses',
    desc: 'Trades, cleaning, landscaping, repair — any business that gets calls and books jobs. Your AI answers every call and fills your calendar automatically.',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
      </svg>
    ),
  },
  {
    title: 'Healthcare & Clinics',
    desc: 'Dental offices, physio, medical clinics — fill your schedule without front desk overflow. AI books and confirms appointments around the clock.',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
      </svg>
    ),
  },
  {
    title: 'Real Estate',
    desc: 'Never lose a lead because you were showing a property. Your AI qualifies inquiries, books showings, and follows up — 24/7.',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    ),
  },
  {
    title: 'Professional Services',
    desc: 'Law firms, accountants, consultants — your AI screens inquiries, books consultations, and keeps prospects engaged until they\'re ready to hire.',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
      </svg>
    ),
  },
];
